import type { Permission } from '../types';

/**
 * Available permissions matching the Casdoor RBAC matrix.
 *
 * `label` is the short form used where space is tight. `sentence` is what the
 * permission lets you actually do, for the Roles & Permissions page — "View
 * Devices" reads as a menu item, not as an answer to "what can I do here".
 *
 * The nine resource groups collapse into six areas: Firmware and Rollouts are
 * one job (Software), and Audit is a log. Fewer headings, same permissions.
 */
export const ALL_PERMISSIONS: {
  value: Permission;
  label: string;
  group: string;
  sentence: string;
}[] = [
  // Device
  { value: 'device:read', label: 'View Devices', group: 'Devices', sentence: 'See the device list and detail' },
  { value: 'device:write', label: 'Edit Devices', group: 'Devices', sentence: 'Edit device details' },

  // Command
  { value: 'command:read', label: 'View Commands', group: 'Commands', sentence: 'See command history' },
  { value: 'command:reboot', label: 'Reboot', group: 'Commands', sentence: 'Reboot a device' },
  { value: 'command:lock', label: 'Lock', group: 'Commands', sentence: 'Lock a device' },
  { value: 'command:factory_reset', label: 'Factory Reset', group: 'Commands', sentence: 'Factory reset a device' },

  // Firmware
  { value: 'firmware:read', label: 'View Firmware', group: 'Software', sentence: 'See firmware and packages' },
  { value: 'firmware:upload', label: 'Upload Firmware', group: 'Software', sentence: 'Upload firmware' },

  // Rollout
  { value: 'rollout:read', label: 'View Rollouts', group: 'Software', sentence: 'See software rollouts' },
  { value: 'rollout:create', label: 'Create Rollouts', group: 'Software', sentence: 'Create a rollout' },

  // Log
  { value: 'log:read', label: 'View Logs', group: 'Logs', sentence: 'Read device logs' },
  { value: 'log:export', label: 'Export Logs', group: 'Logs', sentence: 'Export logs' },

  // Audit
  { value: 'audit:read', label: 'View Audit', group: 'Logs', sentence: 'Read the audit log' },

  // Config
  { value: 'config:read', label: 'View Configurations', group: 'Configuration', sentence: 'See configurations' },
  { value: 'config:write', label: 'Edit Configurations', group: 'Configuration', sentence: 'Edit configurations' },

  // User
  { value: 'user:read', label: 'View Users', group: 'People', sentence: 'See users' },
  { value: 'user:write', label: 'Edit Users', group: 'People', sentence: 'Edit users' },
  { value: 'user:delete', label: 'Delete Users', group: 'People', sentence: 'Remove users' },
  { value: 'user:manage_roles', label: 'Manage Roles', group: 'People', sentence: 'Assign roles' },

  // System
  { value: 'system:admin', label: 'System Administrator', group: 'People', sentence: 'Administer the platform' },
];

// Group permissions by category
export const PERMISSION_GROUPS = ALL_PERMISSIONS.reduce((acc, perm) => {
  if (!acc[perm.group]) {
    acc[perm.group] = [];
  }
  acc[perm.group].push(perm);
  return acc;
}, {} as Record<string, typeof ALL_PERMISSIONS>);
